import { useEffect, useState } from 'react';
import axios from 'axios';
import { Prod01 } from '../constants/ActionType';
import { formatCalendarDate } from './dateFormat';

// Single source of truth for program enrollment + consent display: ProgramEnrollment / ProgramConsent via
// GET /billing/program-status/:patientId. Never read legacy patient.rpmconsent / isCCM / patientType for consent.
export const PROGRAM_STATUS_CHANGED = 'program-status-changed';

export const notifyProgramStatusChanged = (patientId) => {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(PROGRAM_STATUS_CHANGED, { detail: { patientId: String(patientId || '') } }));
};

export const fetchProgramStatus = (patientId) => axios.get(`${Prod01}/billing/program-status/${patientId}`).then(({ data }) => data);

export const useProgramStatus = (patientId) => {
    const [state, setState] = useState({ loading: !!patientId, status: null, error: null });
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const onChange = (e) => { if (!e?.detail?.patientId || e.detail.patientId === String(patientId)) setTick((t) => t + 1); };
        window.addEventListener(PROGRAM_STATUS_CHANGED, onChange);
        return () => window.removeEventListener(PROGRAM_STATUS_CHANGED, onChange);
    }, [patientId]);
    useEffect(() => {
        if (!patientId) return undefined;
        let active = true;
        setState((s) => ({ ...s, loading: true, error: null }));
        fetchProgramStatus(patientId)
            .then((status) => { if (active) setState({ loading: false, status, error: null }); })
            .catch((err) => { if (active) setState({ loading: false, status: null, error: err?.response?.data?.message || err.message }); });
        return () => { active = false; };
    }, [patientId, tick]);
    return state;
};

/** 'RPM, CCM' from active program enrollments. */
export const programsLabel = ({ loading, status, error } = {}) => {
    if (error) return 'Unavailable';
    if (loading && !status) return '…';
    const programs = status?.programs || [];
    return programs.length ? programs.join(', ') : 'None recorded';
};

/** Consent text for one program: 'Signed (verbal, 11/04/2026)', 'Withdrawn (MM/DD/YYYY)', 'Verify at next contact', 'Not recorded'. */
export const consentText = (consent) => {
    switch (consent?.state) {
        case 'signed': return `Signed (${[consent.method, formatCalendarDate(consent.date)].filter(Boolean).join(', ') || 'date not recorded'})`;
        case 'withdrawn': return `Withdrawn${consent.date ? ` (${formatCalendarDate(consent.date)})` : ''}`;
        case 'to_verify': return 'Verify at next contact';
        default: return 'Not recorded';
    }
};

export const consentColor = (consent) => (consent?.state === 'signed' ? 'green' : consent?.state === 'to_verify' ? '#b45309' : 'red');

export const programConsentText = ({ loading, status, error } = {}, program) => {
    if (error) return 'Unavailable';
    if (loading && !status) return '…';
    return consentText(status?.consent?.[program]);
};

export const ConsentStatus = ({ programStatus, program }) => {
    const consent = programStatus?.status?.consent?.[program];
    return <span style={{ color: programStatus?.error ? '#6c757d' : consentColor(consent), fontWeight: 'bold' }} data-testid={`consent-${program}`}>
        {programConsentText(programStatus, program)}
    </span>;
};
